import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, CheckCircle, Clock, Shield, FileText,
  Target, User, ChevronDown, ChevronUp, Download, Search,
  AlertOctagon, Calendar,
} from "lucide-react";

const COMPLIANCE_RULES = {
  weeklyContact: { label: "Weekly Contact", severity: "high", description: "Individual not contacted within required frequency" },
  unsignedNotes: { label: "Unsigned Notes >48hrs", severity: "high", description: "Notes awaiting co-sign beyond 48 hours" },
  missingTxPlan: { label: "No Treatment Plan", severity: "critical", description: "No treatment plan on file — required for Medicaid" },
  noRecentNote: { label: "No Recent Documentation", severity: "medium", description: "No session note in the past 14 days" },
};

function getContactDueDate(individual) {
  const freq = individual.contactFrequency || "weekly";
  const last = individual.lastContactDate ? new Date(individual.lastContactDate) : null;
  if (!last) return { overdue: true, daysOverdue: 999, label: "Never contacted" };
  const days = freq === "weekly" ? 7 : freq === "bi-weekly" ? 14 : freq === "monthly" ? 30 : 7;
  const due = new Date(last.getTime() + days * 24 * 60 * 60 * 1000);
  const now = new Date();
  const diff = Math.floor((now - due) / (1000 * 60 * 60 * 24));
  return { overdue: diff > 0, daysOverdue: diff, dueDate: due, label: diff > 0 ? `${diff}d overdue` : `Due in ${-diff}d` };
}

function getUnsignedNotes(individualId, allNotes) {
  return allNotes.filter(n =>
    n.individualId === individualId &&
    (n.status === "submitted") &&
    !n.supervisorReviewed &&
    n.createdAt?.toDate &&
    (Date.now() - n.createdAt.toDate().getTime()) > 48 * 60 * 60 * 1000
  );
}

function getLastNoteDate(individualId, allNotes) {
  const indNotes = allNotes.filter(n => n.individualId === individualId && (n.status === "submitted" || n.status === "signed"));
  if (indNotes.length === 0) return null;
  const sorted = indNotes.sort((a, b) => {
    const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
    const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
    return bTime - aTime;
  });
  return sorted[0].createdAt?.toDate ? sorted[0].createdAt.toDate() : null;
}

function ComplianceBadge({ status }) {
  const config = {
    compliant: { bg: "rgba(104,211,145,0.1)", color: "#68d391", label: "Compliant" },
    warning: { bg: "rgba(236,201,75,0.1)", color: "#ecc94b", label: "Warning" },
    critical: { bg: "rgba(252,129,129,0.1)", color: "#fc8181", label: "Non-Compliant" },
  };
  const c = config[status] || config.warning;
  return (
    <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 6, background: c.bg, color: c.color, textTransform: "uppercase" }}>
      {c.label}
    </span>
  );
}

export default function ComplianceTab({ C, sharedIndividuals, allNotes, teamMembers }) {
  const navigate = useNavigate();
  const [expandedInd, setExpandedInd] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("flags");

  // Compute compliance for each individual
  const complianceData = useMemo(() => {
    const activeIndividuals = sharedIndividuals.filter(ind => ind.status !== "inactive" && ind.status !== "discharged");

    return activeIndividuals.map(ind => {
      const flags = [];

      // 1. Weekly contact check
      const contact = getContactDueDate(ind);
      if (contact.overdue) {
        flags.push({
          rule: "weeklyContact",
          ...COMPLIANCE_RULES.weeklyContact,
          detail: contact.label,
          daysOverdue: contact.daysOverdue,
        });
      }

      // 2. Unsigned notes >48hrs
      const unsigned = getUnsignedNotes(ind.id, allNotes);
      if (unsigned.length > 0) {
        flags.push({
          rule: "unsignedNotes",
          ...COMPLIANCE_RULES.unsignedNotes,
          detail: `${unsigned.length} note${unsigned.length > 1 ? "s" : ""} unsigned >48hrs`,
          count: unsigned.length,
        });
      }

      // 3. Missing treatment plan
      if (!ind.treatmentPlan || !ind.treatmentPlan.goals || ind.treatmentPlan.goals.length === 0) {
        flags.push({
          rule: "missingTxPlan",
          ...COMPLIANCE_RULES.missingTxPlan,
          detail: "No treatment plan goals on file",
        });
      }

      // 4. No recent documentation (14 days)
      const lastNote = getLastNoteDate(ind.id, allNotes);
      const daysSinceNote = lastNote ? Math.floor((Date.now() - lastNote.getTime()) / (1000 * 60 * 60 * 24)) : 999;
      if (daysSinceNote > 14) {
        flags.push({
          rule: "noRecentNote",
          ...COMPLIANCE_RULES.noRecentNote,
          detail: lastNote ? `Last note ${daysSinceNote}d ago` : "No notes on file",
        });
      }

      // Status
      const hasCritical = flags.some(f => f.severity === "critical");
      const hasHigh = flags.some(f => f.severity === "high");
      const status = flags.length === 0 ? "compliant" : hasCritical ? "critical" : hasHigh ? "warning" : "warning";

      // Score (100 = fully compliant)
      const maxScore = 100;
      const deductions = flags.reduce((sum, f) => sum + (f.severity === "critical" ? 30 : f.severity === "high" ? 20 : 10), 0);
      const score = Math.max(0, maxScore - deductions);

      // Agent
      const agent = teamMembers.find(m => m.id === ind.primaryAgent || m.id === ind.assignedAgent);

      return {
        individual: ind,
        flags,
        status,
        score,
        agentName: agent?.displayName || ind.assignedAgentName || "Unassigned",
        agentId: ind.primaryAgent || ind.assignedAgent,
        contactInfo: contact,
        lastNoteDate: lastNote,
        daysSinceNote,
      };
    });
  }, [sharedIndividuals, allNotes, teamMembers]);

  // Aggregate stats
  const stats = useMemo(() => {
    const total = complianceData.length;
    const compliant = complianceData.filter(c => c.status === "compliant").length;
    const warning = complianceData.filter(c => c.status === "warning").length;
    const critical = complianceData.filter(c => c.status === "critical").length;
    const avgScore = total > 0 ? Math.round(complianceData.reduce((s, c) => s + c.score, 0) / total) : 100;

    const flagCounts = {};
    complianceData.forEach(c => c.flags.forEach(f => {
      flagCounts[f.rule] = (flagCounts[f.rule] || 0) + 1;
    }));

    return { total, compliant, warning, critical, avgScore, flagCounts };
  }, [complianceData]);

  // Agent compliance scores
  const agentScores = useMemo(() => {
    const map = {};
    complianceData.forEach(c => {
      const key = c.agentId || "unassigned";
      if (!map[key]) map[key] = { agentName: c.agentName, scores: [], flags: 0, individuals: 0 };
      map[key].scores.push(c.score);
      map[key].flags += c.flags.length;
      map[key].individuals++;
    });
    return Object.entries(map).map(([id, data]) => ({
      agentId: id,
      agentName: data.agentName,
      avgScore: Math.round(data.scores.reduce((s, v) => s + v, 0) / data.scores.length),
      totalFlags: data.flags,
      individuals: data.individuals,
    })).sort((a, b) => a.avgScore - b.avgScore);
  }, [complianceData]);

  // Filter & sort
  const filtered = useMemo(() => {
    let data = complianceData;
    if (filterStatus === "compliant") data = data.filter(c => c.status === "compliant");
    else if (filterStatus === "warning") data = data.filter(c => c.status === "warning");
    else if (filterStatus === "critical") data = data.filter(c => c.status === "critical");
    if (searchQuery) {
      data = data.filter(c =>
        (c.individual.firstName + " " + c.individual.lastName).toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.agentName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (sortBy === "flags") data = [...data].sort((a, b) => b.flags.length - a.flags.length);
    else if (sortBy === "score") data = [...data].sort((a, b) => a.score - b.score);
    else if (sortBy === "name") data = [...data].sort((a, b) => (a.individual.lastName || "").localeCompare(b.individual.lastName || ""));
    return data;
  }, [complianceData, filterStatus, searchQuery, sortBy]);

  // CSV Export
  const handleExportCSV = () => {
    const rows = [["Individual", "Agent", "Score", "Status", "Flags", "Last Contact", "Last Note", "Treatment Plan"].join(",")];
    complianceData.forEach(c => {
      rows.push([
        `"${c.individual.firstName} ${c.individual.lastName}"`,
        `"${c.agentName}"`,
        c.score,
        c.status,
        `"${c.flags.map(f => f.label).join("; ")}"`,
        c.contactInfo.overdue ? `"Overdue ${c.contactInfo.daysOverdue}d"` : `"On track"`,
        c.lastNoteDate ? `"${c.lastNoteDate.toLocaleDateString()}"` : `"None"`,
        c.individual.treatmentPlan?.goals?.length ? `"${c.individual.treatmentPlan.goals.length} goals"` : `"Missing"`,
      ].join(","));
    });
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `compliance-report-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const scoreColor = (s) => s >= 80 ? "#68d391" : s >= 50 ? "#ecc94b" : "#fc8181";

  return (
    <div>
      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
        {[
          { label: "Avg Score", value: stats.avgScore, suffix: "%", color: scoreColor(stats.avgScore) },
          { label: "Compliant", value: stats.compliant, color: "#68d391" },
          { label: "Warnings", value: stats.warning, color: "#ecc94b" },
          { label: "Critical", value: stats.critical, color: "#fc8181" },
        ].map((s, i) => (
          <div key={i} style={{ ...C.card, textAlign: "center", padding: 12, marginBottom: 0 }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>
              {s.value}{s.suffix || ""}
            </div>
            <div style={{ fontSize: 8, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Flag Breakdown */}
      <div style={{ ...C.card, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: "#a0aec0", marginBottom: 10 }}>Compliance Issues</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {Object.entries(COMPLIANCE_RULES).map(([key, rule]) => {
            const count = stats.flagCounts[key] || 0;
            return (
              <div key={key} style={{
                padding: "8px 12px", borderRadius: 10,
                background: count > 0 ? `${rule.severity === "critical" ? "rgba(252,129,129,0.04)" : "rgba(236,201,75,0.04)"}` : "rgba(104,211,145,0.03)",
                border: `1px solid ${count > 0 ? (rule.severity === "critical" ? "rgba(252,129,129,0.1)" : "rgba(236,201,75,0.1)") : "rgba(104,211,145,0.06)"}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: count > 0 ? (rule.severity === "critical" ? "#fc8181" : "#ecc94b") : "#68d391" }}>
                    {rule.label}
                  </span>
                  <span style={{ fontSize: 16, fontWeight: 900, fontFamily: "monospace", color: count > 0 ? (rule.severity === "critical" ? "#fc8181" : "#ecc94b") : "#68d391" }}>
                    {count}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Agent Compliance Scores */}
      {agentScores.length > 0 && (
        <div style={{ ...C.card, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#a0aec0", marginBottom: 10 }}>Agent Compliance Scores</div>
          {agentScores.map((a, i) => (
            <div key={a.agentId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i < agentScores.length - 1 ? "1px solid rgba(99,179,237,0.04)" : "none" }}>
              <div style={{
                width: 30, height: 30, borderRadius: 8,
                background: `${scoreColor(a.avgScore)}15`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 900, color: scoreColor(a.avgScore), fontFamily: "monospace",
              }}>{a.avgScore}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700 }}>{a.agentName}</div>
                <div style={{ fontSize: 9, color: "#718096" }}>{a.individuals} individual{a.individuals !== 1 ? "s" : ""} · {a.totalFlags} flag{a.totalFlags !== 1 ? "s" : ""}</div>
              </div>
              <div style={{ height: 4, width: 80, borderRadius: 2, background: "rgba(99,179,237,0.06)" }}>
                <div style={{ height: "100%", borderRadius: 2, width: `${a.avgScore}%`, background: scoreColor(a.avgScore), transition: "width 0.3s" }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        {[
          { key: "all", label: "All", count: complianceData.length },
          { key: "critical", label: "Critical", count: stats.critical, color: "#fc8181" },
          { key: "warning", label: "Warnings", count: stats.warning, color: "#ecc94b" },
          { key: "compliant", label: "Compliant", count: stats.compliant, color: "#68d391" },
        ].map(f => (
          <button key={f.key} onClick={() => setFilterStatus(f.key)} style={{
            padding: "5px 12px", borderRadius: 20, fontSize: 10, fontWeight: 700, cursor: "pointer",
            background: filterStatus === f.key ? "rgba(99,179,237,0.15)" : "rgba(15,23,42,0.5)",
            color: filterStatus === f.key ? "#63b3ed" : f.color || "#718096",
            border: `1px solid ${filterStatus === f.key ? "rgba(99,179,237,0.25)" : "rgba(99,179,237,0.06)"}`,
          }}>{f.label} ({f.count})</button>
        ))}
        <div style={{ flex: 1 }} />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{
          padding: "5px 10px", borderRadius: 8, fontSize: 10, fontWeight: 600,
          background: "rgba(15,23,42,0.5)", color: "#718096", border: "1px solid rgba(99,179,237,0.06)", outline: "none",
        }}>
          <option value="flags">Sort: Most Flags</option>
          <option value="score">Sort: Lowest Score</option>
          <option value="name">Sort: Name</option>
        </select>
        <button onClick={handleExportCSV} style={{
          display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 8,
          fontSize: 10, fontWeight: 700, background: "rgba(79,209,197,0.08)", color: "#4fd1c5",
          border: "1px solid rgba(79,209,197,0.12)", cursor: "pointer",
        }}>
          <Download size={10} /> Export CSV
        </button>
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 16 }}>
        <Search size={14} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#4a5568" }} />
        <input style={{ width: "100%", padding: "10px 14px 10px 38px", borderRadius: 12, background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)", color: "#e2e8f0", fontSize: 13, outline: "none" }}
          placeholder="Search by individual or agent name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
      </div>

      {/* Individual Compliance Cards */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <CheckCircle size={32} style={{ color: "#68d391", margin: "0 auto 8px" }} />
          <div style={{ fontSize: 14, fontWeight: 800, color: "#68d391" }}>All Clear</div>
          <div style={{ fontSize: 11, color: "#4a5568", marginTop: 4 }}>No compliance issues match your filter.</div>
        </div>
      ) : (
        filtered.map(comp => {
          const ind = comp.individual;
          const isExpanded = expandedInd === ind.id;
          const name = `${ind.firstName || ""} ${ind.lastName || ""}`.trim() || "Unknown";

          return (
            <div key={ind.id} style={{
              ...C.card, marginBottom: 10, padding: 14,
              borderLeft: `3px solid ${comp.status === "compliant" ? "#68d391" : comp.status === "critical" ? "#fc8181" : "#ecc94b"}`,
              cursor: "pointer",
            }} onClick={() => setExpandedInd(isExpanded ? null : ind.id)}>
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 800 }}>{name}</span>
                    <ComplianceBadge status={comp.status} />
                    <span style={{ fontSize: 14, fontWeight: 900, fontFamily: "monospace", color: scoreColor(comp.score) }}>{comp.score}%</span>
                  </div>
                  <div style={{ fontSize: 10, color: "#718096" }}>
                    Agent: {comp.agentName} · {ind.contactFrequency || "weekly"} contact
                  </div>
                  {/* Flag pills */}
                  {comp.flags.length > 0 && (
                    <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                      {comp.flags.map((f, i) => (
                        <span key={i} style={{
                          fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                          background: f.severity === "critical" ? "rgba(252,129,129,0.1)" : "rgba(236,201,75,0.1)",
                          color: f.severity === "critical" ? "#fc8181" : "#ecc94b",
                        }}>
                          {f.label}: {f.detail}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ color: "#4a5568", marginLeft: 8 }}>
                  {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(99,179,237,0.04)" }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 12 }}>
                    {/* Last Contact */}
                    <div style={{ padding: 10, borderRadius: 10, background: "rgba(15,23,42,0.4)" }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                        <Calendar size={9} style={{ display: "inline", marginRight: 3 }} /> Last Contact
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: comp.contactInfo.overdue ? "#fc8181" : "#68d391" }}>
                        {ind.lastContactDate ? new Date(ind.lastContactDate).toLocaleDateString() : "Never"}
                      </div>
                      <div style={{ fontSize: 10, color: comp.contactInfo.overdue ? "#fc8181" : "#718096" }}>
                        {comp.contactInfo.label}
                      </div>
                    </div>

                    {/* Last Note */}
                    <div style={{ padding: 10, borderRadius: 10, background: "rgba(15,23,42,0.4)" }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                        <FileText size={9} style={{ display: "inline", marginRight: 3 }} /> Last Note
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: comp.daysSinceNote > 14 ? "#fc8181" : "#68d391" }}>
                        {comp.lastNoteDate ? comp.lastNoteDate.toLocaleDateString() : "None"}
                      </div>
                      <div style={{ fontSize: 10, color: comp.daysSinceNote > 14 ? "#fc8181" : "#718096" }}>
                        {comp.lastNoteDate ? `${comp.daysSinceNote}d ago` : "No notes on file"}
                      </div>
                    </div>

                    {/* Treatment Plan */}
                    <div style={{ padding: 10, borderRadius: 10, background: "rgba(15,23,42,0.4)" }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                        <Target size={9} style={{ display: "inline", marginRight: 3 }} /> Treatment Plan
                      </div>
                      {ind.treatmentPlan?.goals?.length ? (
                        <>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#68d391" }}>{ind.treatmentPlan.goals.length} goals</div>
                          <div style={{ fontSize: 10, color: "#718096" }}>
                            {ind.treatmentPlan.goals.filter(g => g.status === "met").length} met · {ind.treatmentPlan.goals.filter(g => g.status === "in-progress").length} active
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#fc8181" }}>Missing</div>
                      )}
                    </div>

                    {/* Unsigned Notes */}
                    <div style={{ padding: 10, borderRadius: 10, background: "rgba(15,23,42,0.4)" }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                        <Clock size={9} style={{ display: "inline", marginRight: 3 }} /> Unsigned Notes
                      </div>
                      {(() => {
                        const unsigned = getUnsignedNotes(ind.id, allNotes);
                        return unsigned.length > 0 ? (
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#fc8181" }}>{unsigned.length} pending &gt;48hrs</div>
                        ) : (
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#68d391" }}>All clear</div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Quick links */}
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => navigate(`/individual/${ind.id}`)} style={{
                      display: "flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 8,
                      fontSize: 10, fontWeight: 700, background: "rgba(99,179,237,0.08)", color: "#63b3ed",
                      border: "1px solid rgba(99,179,237,0.12)", cursor: "pointer",
                    }}>
                      <User size={10} /> Open Profile
                    </button>
                    {(!ind.treatmentPlan?.goals?.length) && (
                      <button onClick={() => navigate(`/individual/${ind.id}`)} style={{
                        display: "flex", alignItems: "center", gap: 4, padding: "6px 12px", borderRadius: 8,
                        fontSize: 10, fontWeight: 700, background: "rgba(252,129,129,0.06)", color: "#fc8181",
                        border: "1px solid rgba(252,129,129,0.1)", cursor: "pointer",
                      }}>
                        <Target size={10} /> Create Tx Plan
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}